import React, { useState, useEffect } from 'react';
import {
  Search, BookOpen, User, Calendar, School, Hash,
  CheckCircle2, AlertCircle, Printer, ChevronRight, Award
} from 'lucide-react';

// ─── Grade calculation helpers ────────────────────────────────────────────────
function getGrade(marks) {
  const n = parseFloat(marks);
  if (isNaN(n)) return { grade: 'NG', point: '0', label: 'Not Graded', color: 'text-gray-500' };
  if (n >= 90) return { grade: 'A+', point: '4.0', label: 'Outstanding',   color: 'text-emerald-600' };
  if (n >= 80) return { grade: 'A',  point: '3.6', label: 'Excellent',     color: 'text-green-600' };
  if (n >= 70) return { grade: 'B+', point: '3.2', label: 'Very Good',     color: 'text-teal-600' };
  if (n >= 60) return { grade: 'B',  point: '2.8', label: 'Good',          color: 'text-blue-600' };
  if (n >= 50) return { grade: 'C+', point: '2.4', label: 'Satisfactory',  color: 'text-indigo-600' };
  if (n >= 40) return { grade: 'C',  point: '2.0', label: 'Acceptable',    color: 'text-amber-600' };
  if (n >= 35) return { grade: 'D+', point: '1.6', label: 'Partially Acceptable', color: 'text-orange-600' };
  if (n >= 28) return { grade: 'D',  point: '1.2', label: 'Insufficient',  color: 'text-red-500' };
  return       { grade: 'E',  point: '0.8', label: 'Very Insufficient', color: 'text-red-700' };
}

function calcGPA(marks) {
  const valid = marks.filter(m => !isNaN(parseFloat(m.marks_in_figure)));
  if (valid.length === 0) return null;
  const sum = valid.reduce((acc, m) => acc + parseFloat(getGrade(m.marks_in_figure).point), 0);
  return (sum / valid.length).toFixed(2);
}

function getOverallGrade(gpa) {
  const n = parseFloat(gpa);
  if (isNaN(n)) return '';
  if (n >= 3.6) return 'Distinction';
  if (n >= 3.2) return 'Credit';
  if (n >= 2.0) return 'Satisfactory';
  if (n >= 1.6) return 'Partial';
  return 'Not Passed';
}

// ─── Step 1: Select Exam Type + Year ─────────────────────────────────────────
function SelectExamStep({ onSelect }) {
  const [published, setPublished] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);

  useEffect(() => {
    fetch('/api/publications/public')
      .then(r => r.json())
      .then(data => { setPublished(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const grouped = published.reduce((acc, p) => {
    if (!acc[p.exam_type]) acc[p.exam_type] = [];
    acc[p.exam_type].push(p);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Result Verification</h1>
          <p className="text-gray-500 text-sm mt-1">National Examinations Board, Nepal</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-1">Select Exam &amp; Year</h2>
          <p className="text-xs text-gray-400 mb-5">Choose the examination and year you want to check results for.</p>

          {loading ? (
            <div className="flex justify-center py-10">
              <span className="w-7 h-7 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : published.length === 0 ? (
            <div className="text-center py-10">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400 opacity-70" />
              <p className="font-semibold text-gray-700 mb-1">Results Not Published Yet</p>
              <p className="text-xs text-gray-400">No results have been published by NEB at this time.<br />Please check back later.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(grouped).map(([examType, years]) => (
                <div key={examType}>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{examType}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {years.map(p => {
                      const isSelected = selected?.exam_type === p.exam_type && selected?.year === p.year;
                      return (
                        <button key={p.year}
                          onClick={() => setSelected(p)}
                          className={`px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all text-left ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-blue-300 text-gray-700'
                          }`}>
                          <div className="text-base">{p.year}</div>
                          <div className="text-xs font-normal text-gray-400 mt-0.5">{examType === 'SEE' ? 'Grade 10' : 'Grade 11/12'}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button
                onClick={() => selected && onSelect(selected)}
                disabled={!selected}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl font-semibold text-sm mt-2 transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
          <button onClick={() => window.location.search = ''} className="hover:text-blue-600 transition-colors">← Back to Home</button>
          <span>·</span>
          <span>Results are official. Contact NEB for discrepancies.</span>
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Enter Symbol Number + DOB ────────────────────────────────────────
function LookupForm({ examType, year, onResult, onBack }) {
  const [symbol, setSymbol] = useState('');
  const [dob, setDob]       = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!symbol.trim() || !dob) { setError('Please enter both Symbol Number and Date of Birth.'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/results/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol_number: symbol.trim(), date_of_birth: dob, exam_type: examType, year })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'not_published') throw new Error(data.message);
        throw new Error(data.error || 'Lookup failed');
      }
      onResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Result Verification</h1>
          <p className="text-gray-500 text-sm mt-1">National Examinations Board, Nepal</p>
        </div>

        {/* Selected exam banner */}
        <div className="flex items-center gap-3 bg-blue-600 text-white rounded-xl px-4 py-3 mb-4">
          <Award className="w-5 h-5 shrink-0 opacity-80" />
          <div>
            <div className="font-bold text-sm">{examType} — {year}</div>
            <div className="text-xs opacity-70">{examType === 'SEE' ? 'Secondary Education Examination' : 'National Examinations Board'}</div>
          </div>
          <button onClick={onBack} className="ml-auto text-xs underline opacity-70 hover:opacity-100">Change</button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-5">Enter your details</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Symbol Number</label>
              <div className="relative">
                <Hash className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={symbol} onChange={e => setSymbol(e.target.value)}
                  placeholder="e.g. 7890123"
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date of Birth</label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="date" value={dob} onChange={e => setDob(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white py-2.5 rounded-xl font-semibold text-sm transition-colors">
              {loading
                ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <><Search className="w-4 h-4" /> View My Result</>}
            </button>
          </form>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
          <button onClick={() => window.location.search = ''} className="hover:text-blue-600 transition-colors">← Back to Home</button>
          <span>·</span>
          <span>Results are official. Contact NEB for discrepancies.</span>
        </div>
      </div>
    </div>
  );
}

// ─── Grade Sheet ──────────────────────────────────────────────────────────────
function GradeSheet({ student, marks, onBack }) {
  const gpa   = calcGPA(marks);
  const overall = gpa ? getOverallGrade(gpa) : '';
  const examType = marks[0]?.exam_type || 'NEB Grade 12';
  const year     = marks[0]?.year || '';

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 print:bg-white print:p-0">
      <div className="max-w-3xl mx-auto">
        {/* Back + Print */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <button onClick={onBack}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline font-medium">
            ← Back to Search
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Printer className="w-4 h-4" /> Print / Download PDF
          </button>
        </div>

        {/* Grade Sheet Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden print:shadow-none print:rounded-none print:border-0">

          {/* Official Header */}
          <div className="bg-blue-700 text-white px-6 py-5 print:bg-white print:text-gray-900 print:border-b-2 print:border-blue-800">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center shrink-0 print:border-2 print:border-blue-800">
                <Award className="w-7 h-7 text-white print:text-blue-800" />
              </div>
              <div>
                <div className="text-xs font-medium opacity-80 print:text-gray-500">Government of Nepal</div>
                <div className="text-lg font-bold leading-tight">National Examinations Board</div>
                <div className="text-sm opacity-90 print:text-gray-700">
                  {examType === 'SEE'
                    ? 'Secondary Education Examination (SEE)'
                    : examType === 'NEB Grade 12'
                    ? 'National Examination Board — Grade 12'
                    : examType}
                  {year ? ` — ${year}` : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Student Info */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <InfoCell icon={<User className="w-3.5 h-3.5" />}    label="Student Name"   value={student.name} />
              <InfoCell icon={<Hash className="w-3.5 h-3.5" />}    label="Symbol Number"  value={student.symbol_number} />
              <InfoCell icon={<Hash className="w-3.5 h-3.5" />}    label="Reg. Number"    value={student.reg_number || '—'} />
              <InfoCell icon={<BookOpen className="w-3.5 h-3.5" />} label="Class"          value={student.class || '—'} />
              <InfoCell icon={<Calendar className="w-3.5 h-3.5" />} label="Date of Birth"  value={student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString('en-GB') : '—'} />
              <InfoCell icon={<School className="w-3.5 h-3.5" />}  label="School"         value={student.school || '—'} />
            </div>
          </div>

          {/* Marks Table */}
          <div className="px-6 py-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Subject-wise Marks</h3>
            {marks.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No marks available yet for this student.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                    <th className="text-left px-3 py-2.5 font-semibold">Subject</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Marks</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Grade</th>
                    <th className="text-center px-3 py-2.5 font-semibold">Points</th>
                    <th className="text-left px-3 py-2.5 font-semibold hidden sm:table-cell">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {marks.map((m, i) => {
                    const g = getGrade(m.marks_in_figure);
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-medium text-gray-800">{m.subject}</td>
                        <td className="px-3 py-2.5 text-center font-semibold text-gray-700">
                          {m.marks_in_figure}
                          {m.marks_in_words && (
                            <div className="text-xs text-gray-400 font-normal">{m.marks_in_words}</div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`text-base font-bold ${g.color}`}>{g.grade}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-600 font-medium">{g.point}</td>
                        <td className={`px-3 py-2.5 text-xs hidden sm:table-cell ${g.color}`}>{g.label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* GPA Summary */}
          {gpa && (
            <div className="mx-6 mb-6 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Final GPA</div>
                <div className="text-3xl font-bold text-blue-700 mt-0.5">{gpa} <span className="text-sm font-normal text-blue-500">/ 4.0</span></div>
                <div className="text-sm font-medium text-blue-800 mt-0.5">{overall}</div>
              </div>
              <CheckCircle2 className="w-12 h-12 text-blue-300" />
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-400 flex items-center justify-between">
            <span>Centre: {marks[0]?.centre || '—'}</span>
            <span>Generated: {new Date().toLocaleDateString('en-GB')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCell({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-gray-400 mt-0.5 shrink-0">{icon}</span>
      <div>
        <div className="text-xs text-gray-400">{label}</div>
        <div className="text-sm font-semibold text-gray-800 leading-tight">{value}</div>
      </div>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function ResultView() {
  const [step, setStep]       = useState('select'); // 'select' | 'lookup' | 'result'
  const [exam, setExam]       = useState(null);     // { exam_type, year }
  const [result, setResult]   = useState(null);

  if (step === 'result' && result) {
    return (
      <GradeSheet
        student={result.student}
        marks={result.marks}
        onBack={() => { setStep('lookup'); setResult(null); }}
      />
    );
  }

  if (step === 'lookup' && exam) {
    return (
      <LookupForm
        examType={exam.exam_type}
        year={exam.year}
        onResult={(data) => { setResult(data); setStep('result'); }}
        onBack={() => setStep('select')}
      />
    );
  }

  return (
    <SelectExamStep
      onSelect={(selected) => { setExam(selected); setStep('lookup'); }}
    />
  );
}
